import React, { useState } from 'react';
import usePWAInstall from '../hooks/usePWAInstall';

export default function InstallBanner() {
  const { canInstall, isInstalled, isIOS, isIOSSafari, triggerInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('ftf_install_dismissed') === '1'
  );
  const [installing, setInstalling] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  if (!canInstall || isInstalled || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem('ftf_install_dismissed', '1');
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (isIOSSafari) { setShowIOSGuide(true); return; }
    setInstalling(true);
    await triggerInstall();
    setInstalling(false);
  };

  return (
    <>
      {/* Main banner */}
      <div style={{
        background: 'linear-gradient(135deg, #13161d 0%, #1a1d26 100%)',
        border: '1px solid rgba(79,126,248,0.3)',
        borderRadius: 16,
        padding: '16px 18px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        position: 'relative',
      }}>
        {/* App icon */}
        <div style={{
          width: 52,
          height: 52,
          borderRadius: 12,
          background: '#4f7ef8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 26,
          flexShrink: 0,
          boxShadow: '0 4px 16px rgba(79,126,248,0.4)',
        }}>🏋️</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#f0f2f7', marginBottom: 3 }}>
            Install Fit to Fit on your phone
          </div>
          <div style={{ fontSize: 12, color: '#8892a4', lineHeight: 1.5 }}>
            {isIOS
              ? 'Add to home screen for quick gym check-ins and workout tracking.'
              : 'Get the full app experience — works offline too.'}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              onClick={handleInstall}
              disabled={installing}
              style={{
                background: '#4f7ef8',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '7px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                opacity: installing ? 0.7 : 1,
              }}
            >
              {installing ? '⏳ Installing…' : isIOS ? '📲 How to install' : '📲 Install app'}
            </button>
            <button
              onClick={dismiss}
              style={{
                background: 'transparent',
                color: '#8892a4',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                padding: '7px 14px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </div>
        </div>

        {/* Features row */}
        <div style={{ position: 'absolute', top: 10, right: 14 }}>
          <button onClick={dismiss} style={{ background: 'none', border: 'none', color: '#525e72', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
      </div>

      {/* iOS install guide modal */}
      {showIOSGuide && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0 0 env(safe-area-inset-bottom)',
        }} onClick={() => setShowIOSGuide(false)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              background: '#13161d',
              borderRadius: '20px 20px 0 0',
              border: '1px solid rgba(255,255,255,0.1)',
              padding: '24px 24px 32px',
            }}
          >
            <div style={{ width: 40, height: 4, background: '#525e72', borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: '#f0f2f7' }}>📱 Add to your home screen</div>
            <div style={{ fontSize: 13, color: '#8892a4', marginBottom: 24 }}>3 quick steps to install Fit to Fit on your iPhone</div>

            {[
              { icon: '⬆️', step: '1', title: 'Tap the Share button', body: 'The share icon at the bottom of Safari (a box with an arrow pointing up).' },
              { icon: '📋', step: '2', title: 'Tap "Add to Home Screen"', body: 'Scroll down in the share menu until you see this option.' },
              { icon: '✅', step: '3', title: 'Tap "Add"', body: 'The Fit to Fit icon will appear on your home screen — just like a real app.' },
            ].map(item => (
              <div key={item.step} style={{
                display: 'flex',
                gap: 14,
                marginBottom: 18,
                padding: '14px 16px',
                background: '#1a1d26',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{item.icon}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#f0f2f7', marginBottom: 4 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: '#8892a4', lineHeight: 1.5 }}>{item.body}</div>
                </div>
              </div>
            ))}

            <button
              onClick={() => setShowIOSGuide(false)}
              style={{
                width: '100%',
                background: '#4f7ef8',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '13px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                marginTop: 6,
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
